import {Component, EnvironmentInjector, OnDestroy, OnInit, signal} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {ConnectionStatus, Network} from '@capacitor/network';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet} from '@angular/router';
import {UpdateService} from '@core/services/update.service';
import {AppClientMetaService} from '@core/services/app-client-meta.service';
import {MaintenanceViewModel} from '@core/models';
import {Observable, Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Capacitor} from "@capacitor/core";
import {StatusBar, Style} from "@capacitor/status-bar";
import {SplashScreen} from "@capacitor/splash-screen";
import {ModalController, Platform} from '@ionic/angular';
import {MaintenanceViewComponent} from '@shared/components';
import {ToastProvider} from '@core/providers/toast.provider';
import {isChunkLoadFailure} from '@core/utils/chunk-load-failure.util';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    RouterOutlet,
    AsyncPipe,
    MaintenanceViewComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribe$$: Subject<void> = new Subject();
  private backButtonSub?: Subscription;
  private maintenanceActive = false;
  private navSerial = 0;
  private navTimer?: ReturnType<typeof setTimeout>;
  readonly routeLoading = signal(false);
  maintenance$!: Observable<MaintenanceViewModel | null>;
  constructor(
    private router: Router,
    public environmentInjector: EnvironmentInjector,
    private updateService: UpdateService,
    private platform: Platform,
    private modalCtrl: ModalController,
    private appClientMeta: AppClientMetaService,
    private toastProvider: ToastProvider,
  ) {
    void this.appClientMeta.ensureLoaded();
    this.maintenance$ = this.updateService.maintenance$;
    this.updateService.checkAppVersion().pipe(
      takeUntil(this.unsubscribe$$)
    ).subscribe();
    this.maintenance$.pipe(
      takeUntil(this.unsubscribe$$)
    ).subscribe((maintenance: MaintenanceViewModel | null) => {
      this.maintenanceActive = maintenance != null;
      if (this.maintenanceActive) {
        this.modalCtrl.dismiss().catch(() => undefined);
      }
    });
    this.router.events.pipe(
      takeUntil(this.unsubscribe$$)
    ).subscribe((event) => this.onNavigation(event));
  }

  private onNavigation(event: unknown): void {
    if (event instanceof NavigationStart) {
      this.routeLoading.set(false);
      this.clearNavTimer();
      const navId = ++this.navSerial;
      this.navTimer = setTimeout(() => {
        if (this.navSerial === navId) {
          this.routeLoading.set(true);
        }
      }, 200);
      return;
    }
    if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      this.navSerial++;
      this.clearNavTimer();
      this.routeLoading.set(false);
    }
    if (event instanceof NavigationError && isChunkLoadFailure(event.error)) {
      void this.toastProvider.presentToast({
        message: 'Couldn\'t open that page. Check your connection and try again.',
        color: 'danger',
      });
    }
  }

  private clearNavTimer(): void {
    if (this.navTimer !== undefined) {
      clearTimeout(this.navTimer);
      this.navTimer = undefined;
    }
  }

  ngOnInit() {
    this.backButtonSub = this.platform.backButton.subscribeWithPriority(9999, (processNextHandler) => {
      if (this.maintenanceActive) {
        return;
      }
      processNextHandler();
    });
    // this.networkStatusChange().then();
    if (Capacitor.getPlatform() !== 'web') {
      StatusBar.setOverlaysWebView({ overlay: true }).then();
      // Style.Dark = dark icons on a light background (not a dark status bar).
      StatusBar.setStyle({ style: Style.Dark }).then();
      StatusBar.setBackgroundColor({ color: '#f3f5f7' }).then();
      SplashScreen.hide().then();
    }
  }

  async networkStatusChange() {
    Network.addListener('networkStatusChange', status => {
      this.networkStatus(status);
    });
  }

  networkStatus(status: ConnectionStatus) {
    if (!status.connected) {
      this.router.navigate(['/no-internet']).then(r => r);
    } else {
      this.router.navigate(['/']).then(r => r);
    }
  }

  ngOnDestroy(): void {
    this.clearNavTimer();
    this.backButtonSub?.unsubscribe();
    this.unsubscribe$$.next();
    this.unsubscribe$$.complete();
  }
}
